/**
 * Store (shop) sidebar badge store.
 * Written by `<StoreBadgeSubscriber />`; read by `storeMenu`.
 *
 * Counts are deliberately the ones a shop admin has to ACT on — unpaid orders, orders
 * waiting to be handed over, stock that has run out. A badge showing "how many orders
 * exist" is decoration; a badge showing "how many are unpaid" is a to-do list.
 */

import { create } from 'zustand';

export interface StoreBadgeCounts {
  /**
   * Awaiting Payment (1) + Paid (2) — the orders someone still has to act on.
   * This is what the "New Orders" badge shows, and it is deliberately NOT the total
   * order count: 908 orders is a fact, not a task list.
   */
  newOrders: number;
  /** Shop orders with nothing settled against them. */
  unpaidOrders: number;
  /** Paid but not yet collected — the fulfilment queue. */
  awaitingFulfilment: number;
  /** Bagged and waiting on the shelf. */
  readyForPickup: number;
  /** Stock lines at or below their minimum. */
  lowStock: number;
  /** Stock lines at zero. */
  outOfStock: number;
}

interface StoreBadgesState extends StoreBadgeCounts {
  setFromMetrics: (m: Partial<StoreBadgeCounts>) => void;
  applyDelta: (m: Partial<StoreBadgeCounts>) => void;
  reset: () => void;
}

const EMPTY: StoreBadgeCounts = {
  newOrders: 0,
  unpaidOrders: 0,
  awaitingFulfilment: 0,
  readyForPickup: 0,
  lowStock: 0,
  outOfStock: 0,
};

export const useStoreBadgesStore = create<StoreBadgesState>((set) => ({
  ...EMPTY,

  setFromMetrics: (m) =>
    set({
      newOrders: m.newOrders ?? 0,
      unpaidOrders: m.unpaidOrders ?? 0,
      awaitingFulfilment: m.awaitingFulfilment ?? 0,
      readyForPickup: m.readyForPickup ?? 0,
      lowStock: m.lowStock ?? 0,
      outOfStock: m.outOfStock ?? 0,
    }),

  // Deltas are signed and applied in place. Floored at zero: a delta that arrives twice,
  // or after a snapshot already accounted for it, must not drive a badge negative.
  applyDelta: (m) =>
    set((state) => ({
      newOrders: Math.max(0, state.newOrders + (m.newOrders ?? 0)),
      unpaidOrders: Math.max(0, state.unpaidOrders + (m.unpaidOrders ?? 0)),
      awaitingFulfilment: Math.max(0, state.awaitingFulfilment + (m.awaitingFulfilment ?? 0)),
      readyForPickup: Math.max(0, state.readyForPickup + (m.readyForPickup ?? 0)),
      lowStock: Math.max(0, state.lowStock + (m.lowStock ?? 0)),
      outOfStock: Math.max(0, state.outOfStock + (m.outOfStock ?? 0)),
    })),

  reset: () => set(EMPTY),
}));
